import { TrendingUp } from "lucide-react";

/**
 * UsEarningsComparison — 1y → 3y salary card + employer-lift
 * overlay for US degrees. Mirrors the LeoComparison card used on UK
 * degrees but reads the editorial USD fields on `outcomes` instead
 * of HESA LEO GBP fields on `hesa_stats`.
 *
 * Rendered from DegreeDetail when the id starts with "us-" AND both
 * starting + 3-yr salary datapoints are present. The overlay row
 * mirrors LeoComparison exactly so a family reading both feels
 * consistent — purple tick = course average, green ticks = named
 * employers.
 */
export default function UsEarningsComparison({ outcomes, employerLifts }) {
  const early = outcomes?.median_starting_salary_usd;
  const later = outcomes?.salary_after_3y_usd;
  if (!early && !later) return null;
  const growth = early && later ? Math.round(((later - early) / early) * 100) : null;

  const lifts = employerLifts && typeof employerLifts === "object"
    ? Object.values(employerLifts).filter((v) => v && typeof v.lift_pct === "number")
    : [];
  const overlayMax = Math.max(growth ?? 0, ...lifts.map((l) => l.lift_pct), 10);
  const fmt = (v) => (v ? `$${v.toLocaleString()}` : "—");

  return (
    <div data-testid="us-earnings-comparison" className="mt-5 rounded-lg border border-stone-200 bg-white p-4">
      <div className="label-uppercase text-stone-600 mb-3 flex items-center gap-1.5">
        <TrendingUp className="h-3 w-3" /> Median salary: starting → 3-year (editorial estimate)
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div data-testid="us-early-block">
          <div className="text-[11px] font-underground-sc tracking-wider text-stone-500">Starting salary</div>
          <div className="font-chip text-2xl font-semibold text-stone-900 mt-1">{fmt(early)}</div>
        </div>
        <div data-testid="us-later-block">
          <div className="text-[11px] font-underground-sc tracking-wider text-stone-500">3 years after graduation</div>
          <div className="font-chip text-2xl font-semibold text-[#4C1D95] mt-1">{fmt(later)}</div>
        </div>
      </div>
      {growth != null && (
        <div className="mt-3 text-sm">
          <span className="label-uppercase !text-[10px] mr-2">Growth over year 1–3</span>
          <span className={`font-mono font-semibold ${growth >= 0 ? "text-emerald-700" : "text-red-700"}`}>
            {growth >= 0 ? "+" : ""}{growth}%
          </span>
        </div>
      )}

      {/* Same overlay pattern as LeoComparison — purple tick for the
          course average, green ticks for each employer's aggregated
          lift so families instantly see per-firm progression. */}
      {lifts.length > 0 && growth != null && (
        <div className="mt-5 pt-4 border-t border-stone-100" data-testid="us-employer-overlay">
          <div className="label-uppercase text-stone-500 mb-3 !text-[10px]">
            How top employers' grads compare on lift
          </div>
          <div className="relative h-8">
            <div className="absolute inset-x-0 top-3 h-1 rounded-full bg-stone-100" />
            <div
              className="absolute top-2 h-3 w-0.5 bg-[#4C1D95] rounded-sm"
              style={{ left: `${Math.min(100, (growth / overlayMax) * 100)}%` }}
              title={`Course average · +${growth}%`}
              aria-hidden
            />
            {lifts.map((l) => (
              <div
                key={l.employer}
                data-testid={`us-employer-tick-${l.employer.replace(/\s+/g, '-').toLowerCase()}`}
                className="absolute top-2 h-3 w-0.5 bg-emerald-600 rounded-sm cursor-help"
                style={{ left: `${Math.min(100, (l.lift_pct / overlayMax) * 100)}%` }}
                title={`${l.employer} · +${l.lift_pct}%`}
              />
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-mono text-stone-600">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-0.5 h-3 bg-[#4C1D95]" /> course +{growth}%
            </span>
            {lifts.map((l) => (
              <span key={l.employer} className="inline-flex items-center gap-1">
                <span className="inline-block w-0.5 h-3 bg-emerald-600" />
                {l.employer} +{l.lift_pct}%
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="text-[10px] font-mono text-stone-400 mt-2">
        Editorial per-degree data · WhichUniversityAdvisor
      </div>
    </div>
  );
}
